import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, CssBaseline } from '@mui/material';
import MapView from './MapView.jsx';
import TopToolbar from './TopToolbar.js';
import PropertySlider from './PropertySlider.jsx';
import PropertyModal from './PropertyModal.jsx';
import { getFilterOptions } from './api.js';
import CustomBottomNav from "./../Buy Module/CustomNav";
import { getUserWishlist, addToWishlist, removeFromWishlist } from './api.js';

const PRICE_MAX = 100000000000;  
const Index = () => {
  const savedMapState = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('buymapState') || 'null');
    } catch {
      return null;
    }
  }, []);

  const [viewport, setViewport] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('buymapState') || 'null');
      if (saved && saved.zoom) {
        return {
          bounds: { south: 6.5, north: 35.5, west: 68, east: 97.5 },
          zoom: saved.zoom,
        };
      }
      return {
        bounds: { south: 6.5, north: 35.5, west: 68, east: 97.5 },
        zoom: 5,
      };
    } catch {
      return {
        bounds: { south: 6.5, north: 35.5, west: 68, east: 97.5 },
        zoom: 5,
      };
    }
  });

  const [categories, setCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, PRICE_MAX]);
  const [visibleProperties, setVisibleProperties] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [modalProperty, setModalProperty] = useState(null);

  const [flyTarget, setFlyTarget] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('buymapState') || 'null');
      if (saved && saved.lat && saved.lng && saved.zoom) {
        return { 
          lat: saved.lat, 
          lng: saved.lng, 
          zoom: saved.zoom, 
          key: Date.now() 
        };
      }
    } catch {
      // ignore
    }
    return null;
  });

  const [totalCount, setTotalCount] = useState(0);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryColorMap, setCategoryColorMap] = useState({});
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showAlert, setShowAlert] = useState(null);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState({});
  const sliderRef = useRef(null);

  useEffect(() => {
    const loadOptions = async () => {
      const data = await getFilterOptions();
      console.log('Filter options:', data);
      if (data.success && data.priceRange) {
        setPriceRange([data.priceRange.min, data.priceRange.max]);
        setTotalCount(data.totalProperties);
        setCategoryOptions(data.categories || []);

        const colorPalette = [
          '#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#E53935',
          '#00BCD4', '#FF5722', '#795548', '#607D8B', '#E91E63',
          '#8BC34A', '#009688', '#FFC107', '#673AB7', '#F44336',
          '#3F51B5', '#CDDC39', '#FFEB3B', '#9E9E9E', '#D32F2F',
          '#7B1FA2', '#C2185B', '#0288D1', '#00796B', '#F57C00',
          '#455A64', '#6A1B9A', '#AD1457', '#00695C', '#E65100'
        ];

        const colorMap = {};
        (data.categories || []).forEach((category, index) => {
          const categoryValue = category.value || category;
          colorMap[categoryValue] = colorPalette[index % colorPalette.length];
        });
        setCategoryColorMap(colorMap);
      }
    };
    loadOptions();
  }, []);

  const filters = useMemo(() => ({
    propertyType: categories,
    priceMin: priceRange[0],
    priceMax: priceRange[1],
  }), [categories, priceRange]);

  const handleViewportChange = useCallback((vp) => {
    setViewport(vp);
  }, []);

  const handlePropertiesUpdate = useCallback((props, showAlertFn) => {
    console.log('Properties received:', props.length);
    setVisibleProperties(props);
    if (showAlertFn) {
      setShowAlert(() => showAlertFn);
    }
  }, []);

  const handleLocationSelect = useCallback((loc) => {
    setSelectedLocation(loc);
    if (loc) {
      const lat = loc.lat ?? loc.latitude;
      const lng = loc.lng ?? loc.lon ?? loc.longitude;
      if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
        setFlyTarget({ lat: parseFloat(lat), lng: parseFloat(lng), zoom: 13, key: Date.now() });
      } else {
        console.warn('Invalid location coords:', loc);
      }
    }
  }, []);

  useEffect(() => {
    const loadWishlist = async () => {
      const userId = localStorage.getItem('userId');
      if (userId) {
        const result = await getUserWishlist(userId);
        if (result.success) {
          setWishlistItems(result.data);
        }
      }
    };
    loadWishlist();
  }, []);

  const isPropertySaved = useCallback((property) => {
    return wishlistItems.some(item => item.property_id === property.id);
  }, [wishlistItems]);

  const handleWishlistToggle = useCallback(async (property) => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;

    setWishlistLoading(prev => ({ ...prev, [property.id]: true }));

    try {
      const isSaved = wishlistItems.some(item => item.property_id === property.id);

      if (isSaved) {
        const cartItem = wishlistItems.find(item => item.property_id === property.id);
        const result = await removeFromWishlist(cartItem.cart_id);
        if (result.success) {
          setWishlistItems(prev => prev.filter(item => item.property_id !== property.id));
          if (showAlert) showAlert('success', 'Removed from wishlist');
        }
      } else {
        const result = await addToWishlist(userId, property.id);
        if (result.success) {
          setWishlistItems(prev => [...prev, result.data]);
          if (showAlert) showAlert('success', 'Added to wishlist');
        }
      }
    } catch (error) {
      console.error('Wishlist error:', error);
      if (showAlert) showAlert('failure', 'Failed to update wishlist');
    } finally {
      setWishlistLoading(prev => ({ ...prev, [property.id]: false }));
    }
  }, [wishlistItems, showAlert]);

  const handleClearLocation = useCallback(() => {
    setSelectedLocation(null);
    setFlyTarget({ lat: 20.5937, lng: 78.9629, zoom: 5, key: Date.now() });
  }, []);

  const handleMarkerClick = useCallback((p) => {
    console.log('Marker clicked:', p.id);
    setSelectedId(p.id);

    const currentZoom = viewport.zoom;

    const stateToSave = {
      lat: p.lat,
      lng: p.lng,
      zoom: currentZoom,
      timestamp: Date.now(),
    };
    
    localStorage.setItem('primestate', JSON.stringify(stateToSave));
    localStorage.setItem('lastViewedPropertyId', p.id.toString());

    setFlyTarget({
      lat: p.lat,
      lng: p.lng,
      zoom: currentZoom,
      key: Date.now()
    });

    setTimeout(() => {
      if (sliderRef.current) {
        const index = sliderRef.current.getCardIndex?.(p.id);
        if (index !== undefined && index !== -1) {
          sliderRef.current.scrollToCard(index);
        }
      }
    }, 200);
  }, [viewport.zoom]);

  const hasRestoredViewRef = useRef(false); 

  useEffect(() => {
    if (hasRestoredViewRef.current) return;
    const lastViewedId = localStorage.getItem('lastViewedPropertyId');
    if (lastViewedId && visibleProperties.length > 0) {
      const id = parseInt(lastViewedId);
      const property = visibleProperties.find(p => p.id === id);
      if (property) {
        hasRestoredViewRef.current = true;
        setSelectedId(id);
        
        let restoreZoom = viewport.zoom || 5;
        try {
          const savedState = JSON.parse(localStorage.getItem('buymapState') || 'null');
          if (savedState?.zoom) {
            restoreZoom = savedState.zoom;
          }
        } catch {
          // ignore
        }
        
        setFlyTarget({
          lat: property.lat,
          lng: property.lng,
          zoom: restoreZoom, // ✅ Restore to original zoom
          key: Date.now()
        });
      }
    }
  }, [visibleProperties, viewport.zoom]);

 const handleCardSelect = useCallback((p) => {
  setSelectedId(p.id);

  const currentZoom = viewport.zoom;

  const stateToSave = {
    lat: p.lat,
    lng: p.lng,
    zoom: currentZoom, 
    timestamp: Date.now(),
  };

  localStorage.setItem('buymapState', JSON.stringify(stateToSave));
  localStorage.setItem('lastViewedPropertyId', p.id.toString());

  setFlyTarget({
    lat: p.lat,
    lng: p.lng,
    zoom: currentZoom, 
    key: Date.now()
  });
}, [viewport.zoom]);

  const handleCardInView = useCallback((p) => {
    setSelectedId(p.id);
  }, []);

  const handlePriceRangeChange = useCallback((newRange) => {
    console.log('Price range changed:', newRange);
    setPriceRange(newRange);
  }, []);

  useEffect(() => {
    let timer;

    const clearData = () => {
      const hasState = localStorage.getItem('buymapState');
      if (!hasState) return;

      try {
        const state = JSON.parse(hasState);
        if (state.timestamp && Date.now() - state.timestamp > 60 * 60 * 1000) {
          localStorage.removeItem('buymapState');
          
          setSelectedLocation(null);
          setFlyTarget({ lat: 20.5937, lng: 78.9629, zoom: 5, key: Date.now() });
        }
      } catch {
        localStorage.removeItem('buymapState');
      }
    };

    const startTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(clearData, 60 * 60 * 1000);
    };

    startTimer();

    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      startTimer();
    };

    window.addEventListener('click', resetTimer);
    window.addEventListener('scroll', resetTimer);
    window.addEventListener('keypress', resetTimer);

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('scroll', resetTimer);
      window.removeEventListener('keypress', resetTimer);
    };
  }, []);

  return (
    <>
      <CssBaseline />
      <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', bgcolor: 'transparent' }}>
        <MapView
          onViewportChange={handleViewportChange}
          onPropertiesUpdate={handlePropertiesUpdate}
          filters={filters}
          selectedId={selectedId}
          onMarkerClick={handleMarkerClick}
          flyTarget={flyTarget}
          categoryColorMap={categoryColorMap}
        />
        <TopToolbar
          categoryOptions={categoryOptions}
          categories={categories}
          onCategoriesChange={setCategories}
          priceRange={priceRange}
          onPriceRangeChange={handlePriceRangeChange}
          onLocationSelect={handleLocationSelect}
          onClearLocation={handleClearLocation}
          selectedLocation={selectedLocation}
        />
        <PropertySlider
          ref={sliderRef}
          properties={visibleProperties}
          selectedId={selectedId}
          onSelect={handleCardSelect}
          onView={setModalProperty}
          onCardInView={handleCardInView}
          showAlert={showAlert}
          wishlistItems={wishlistItems}
          onWishlistToggle={handleWishlistToggle}
        />
        <PropertyModal property={modalProperty} onClose={() => setModalProperty(null)} />
      </Box>
      <CustomBottomNav />
    </>
  );
};

export default Index;